/**
 * EnemyShapes — BL-51: per-archetype procedural silhouettes ("malware
 * visualised"). Mirrors the per-element `TowerNodeShapes` pattern, but for
 * enemies: one pure path-builder per family that lays down the body path at
 * `(ex, ey, r)` for the caller to fill.
 *
 * These builders are deliberately **colour-agnostic** — they only `moveTo`/
 * `lineTo`/`arc` to construct subpaths. The caller sets `ctx.fillStyle` to the
 * shared emissive gradient (`emissiveFill(ctx, ex, ey, r, deriveRamp(color))`)
 * and calls `ctx.fill()` once, so every silhouette is lit by the same S79
 * material (BL-26/27). Multi-part forms (cluster/worm/boss) emit several
 * subpaths into a single path, so one fill lights them as one volume — parts
 * past the gradient's outer radius read as the dark `rim`, which reinforces
 * depth (a worm's tail fades, a boss's outer armour grounds).
 *
 * Orientation: directional families (dart/ram/worm) take a `heading` (radians,
 * travel direction) and are built in world space via cos/sin rotation — no
 * `ctx` transform, so the pre-built world-space gradient stays aligned. The
 * remaining families ignore `heading` (their read doesn't depend on facing).
 *
 * Animation is keyed off `time` (+ the body's x, for per-enemy phase) only —
 * never `Math.random()` — so the renderer can rebuild the identical path at a
 * smaller scale for the inner-edge highlight.
 */

export type EnemyShapeFn = (
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
  heading: number,
) => void;

// ─── Local helpers ──────────────────────────────────────────────────────────

/** A standalone circle subpath (moveTo to the rim first so it doesn't link to
 *  the previous subpath with a stray chord). */
function circle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rad: number,
): void {
  ctx.moveTo(cx + rad, cy);
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
}

/** A regular n-gon subpath centred at (cx, cy). */
function polygon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rad: number,
  sides: number,
  rot: number,
): void {
  for (let j = 0; j < sides; j++) {
    const a = (j / sides) * Math.PI * 2 + rot;
    const x = cx + Math.cos(a) * rad;
    const y = cy + Math.sin(a) * rad;
    if (j === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** A small diamond subpath (the classic body, reused as a sub-part). */
function diamondAt(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rad: number,
): void {
  ctx.moveTo(cx, cy - rad);
  ctx.lineTo(cx + rad * 0.7, cy);
  ctx.lineTo(cx, cy + rad);
  ctx.lineTo(cx - rad * 0.7, cy);
  ctx.closePath();
}

/** moveTo a local (lx forward, ly lateral) point rotated by heading (c,s). */
function rMoveTo(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  c: number,
  s: number,
  lx: number,
  ly: number,
): void {
  ctx.moveTo(ex + lx * c - ly * s, ey + lx * s + ly * c);
}

/** lineTo a local (lx forward, ly lateral) point rotated by heading (c,s). */
function rLineTo(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  c: number,
  s: number,
  lx: number,
  ly: number,
): void {
  ctx.lineTo(ex + lx * c - ly * s, ey + lx * s + ly * c);
}

// ─── Shape builders ─────────────────────────────────────────────────────────

/** Fallback — the classic vertical diamond (width 1.4r, height 2r). */
export function buildDiamond(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
): void {
  diamondAt(ctx, ex, ey, r);
}

/** Data Packet — a sharp forward chevron/arrowhead with a notched tail. */
export function buildDart(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  _time: number,
  heading: number,
): void {
  const c = Math.cos(heading);
  const s = Math.sin(heading);
  rMoveTo(ctx, ex, ey, c, s, 1.4 * r, 0); // tip (forward)
  rLineTo(ctx, ex, ey, c, s, -0.55 * r, 0.78 * r); // right barb
  rLineTo(ctx, ex, ey, c, s, -0.15 * r, 0); // concave tail notch → chevron
  rLineTo(ctx, ex, ey, c, s, -0.55 * r, -0.78 * r); // left barb
  ctx.closePath();
}

/** Rootkit (tank) — a squat, flat-topped armoured hexagon: reads as mass. */
export function buildBlock(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
): void {
  for (let j = 0; j < 6; j++) {
    const a = (j / 6) * Math.PI * 2;
    const x = ex + Math.cos(a) * r * 1.04; // slightly wider …
    const y = ey + Math.sin(a) * r * 0.9; // … than tall → squat
    if (j === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** Nano-Swarm — NOT a solid: a centre cell ringed by jittering satellites. */
export function buildCluster(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
): void {
  circle(ctx, ex, ey, r * 0.4);
  const cells = 5;
  for (let i = 0; i < cells; i++) {
    const ang =
      (i / cells) * Math.PI * 2 +
      time * 0.6 +
      Math.sin(time * 2 + i * 1.3 + ex * 0.05) * 0.15;
    const dist = r * (0.6 + Math.sin(time * 3 + i * 1.7 + ex * 0.05) * 0.12);
    circle(ctx, ex + Math.cos(ang) * dist, ey + Math.sin(ang) * dist, r * 0.3);
  }
}

/** Polymorphic Worm — a segmented chain that slithers, head→tail tapering. */
export function buildWorm(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
  heading: number,
): void {
  const c = Math.cos(heading);
  const s = Math.sin(heading);
  // [forward offset, radius] per segment, head (front) → tail (back).
  const segs: ReadonlyArray<readonly [number, number]> = [
    [0.85, 0.5],
    [0.2, 0.52],
    [-0.45, 0.45],
    [-1.1, 0.34],
  ];
  for (let i = 0; i < segs.length; i++) {
    const lx = segs[i]![0] * r;
    // lateral slither, growing toward the tail
    const ly = Math.sin(time * 4 + i * 0.9 + ex * 0.05) * 0.16 * r * i;
    const cx = ex + lx * c - ly * s;
    const cy = ey + lx * s + ly * c;
    circle(ctx, cx, cy, segs[i]![1] * r);
  }
}

/** Corrupted Node / Cipher Breaker — an angular, asymmetric, flickering glyph. */
export function buildGlyph(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
): void {
  // Irregular [angle, radius-factor] facets → a fractured "encrypted" shard.
  const verts: ReadonlyArray<readonly [number, number]> = [
    [0, 1.0],
    [0.95, 0.62],
    [1.9, 1.05],
    [2.7, 0.5],
    [3.5, 0.95],
    [4.6, 0.58],
    [5.5, 0.88],
  ];
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i]![0];
    const flick = Math.sin(time * 7 + i * 2.1 + ex * 0.1) * 0.05;
    const rr = r * (verts[i]![1] + flick);
    const x = ex + Math.cos(a) * rr;
    const y = ey + Math.sin(a) * rr;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** Firewall Breaker — a heavy forward battering wedge (broad blunt-backed plow). */
export function buildRam(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  _time: number,
  heading: number,
): void {
  const c = Math.cos(heading);
  const s = Math.sin(heading);
  rMoveTo(ctx, ex, ey, c, s, 1.2 * r, 0); // nose
  rLineTo(ctx, ex, ey, c, s, 0.4 * r, 0.72 * r);
  rLineTo(ctx, ex, ey, c, s, -0.9 * r, 0.56 * r);
  rLineTo(ctx, ex, ey, c, s, -0.9 * r, -0.56 * r);
  rLineTo(ctx, ex, ey, c, s, 0.4 * r, -0.72 * r);
  ctx.closePath();
}

/** Stealth Daemon (BL-49) — a hooded, broad-shouldered wraith tapering to a
 *  blade tail: a cloaked predator that reads as clearly NOT the diamond and NOT
 *  any dispatch family. Deliberately **omitted from `ENEMY_SHAPES`** — stealth
 *  routes through `StealthRenderer` (cloak / white-hot reveal), which imports
 *  this builder directly. Vertical (heading-agnostic, like `buildDiamond`) so
 *  the cloak's displaced shimmer copies and the reveal's white-hot blowout can
 *  rebuild the identical path at any scale. Built with quadratic curves for an
 *  organic hood/cloak read (the angular families use straight facets). */
export function buildPhantom(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
): void {
  const topY = ey - 1.25 * r; // hood apex
  const tailY = ey + 1.3 * r; // blade tail
  const shX = 0.95 * r; // shoulder-hook reach
  const shY = ey - 0.02 * r; // shoulder height (≈ centre)
  const waistX = 0.3 * r; // cinched cloak waist
  const waistY = ey + 0.55 * r;

  ctx.moveTo(ex, topY);
  // apex → right shoulder hook (control pulled inward+up → narrow hood)
  ctx.quadraticCurveTo(ex + 0.34 * r, ey - 1.05 * r, ex + shX, shY);
  // right shoulder → waist (cloak drapes inward)
  ctx.quadraticCurveTo(ex + 0.82 * r, ey + 0.26 * r, ex + waistX, waistY);
  // waist → blade tail
  ctx.quadraticCurveTo(ex + 0.4 * r, ey + 1.0 * r, ex, tailY);
  // blade tail → left waist (mirror)
  ctx.quadraticCurveTo(ex - 0.4 * r, ey + 1.0 * r, ex - waistX, waistY);
  // left waist → left shoulder hook
  ctx.quadraticCurveTo(ex - 0.82 * r, ey + 0.26 * r, ex - shX, shY);
  // left shoulder → apex
  ctx.quadraticCurveTo(ex - 0.34 * r, ey - 1.05 * r, ex, topY);
  ctx.closePath();
}

/** Corrupted Node (S154) — a clean octagonal data-hub with a subtle corruption
 *  flicker. Replaces the retired `glyph` collision (S152 flagged corrupted_node
 *  + cipher_breaker both reading as `glyph`): cipher_breaker keeps the irregular
 *  `glyph` shard, this reads distinctly as a regular faceted "node". */
export function buildNode(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
): void {
  const sides = 8;
  for (let j = 0; j < sides; j++) {
    const a = (j / sides) * Math.PI * 2 + Math.PI / 8; // flat-top octagon
    const flick = 1 + Math.sin(time * 5 + j * 1.7 + ex * 0.08) * 0.04;
    const x = ex + Math.cos(a) * r * flick;
    const y = ey + Math.sin(a) * r * flick;
    if (j === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** Jammer (S154, Act II) — a radial broadcast emitter: a pentagonal core ringed
 *  by pulsing antenna spikes. Reads as "this thing is jamming a signal" — clearly
 *  NOT the round cluster nor any directional wedge. Slow spin + a broadcast pulse
 *  key off `time` (+ x phase), never random, so the inner-edge rebuild matches. */
export function buildJammer(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
): void {
  const spikes = 5;
  const spin = time * 0.5;
  const pulse = 1 + Math.sin(time * 4 + ex * 0.05) * 0.08; // broadcast pulse
  polygon(ctx, ex, ey, r * 0.44, 5, spin); // pentagonal core
  for (let i = 0; i < spikes; i++) {
    const a = (i / spikes) * Math.PI * 2 + spin;
    const tipX = ex + Math.cos(a) * r * 1.18 * pulse;
    const tipY = ey + Math.sin(a) * r * 1.18 * pulse;
    ctx.moveTo(
      ex + Math.cos(a + 0.2) * r * 0.36,
      ey + Math.sin(a + 0.2) * r * 0.36,
    );
    ctx.lineTo(tipX, tipY);
    ctx.lineTo(
      ex + Math.cos(a - 0.2) * r * 0.36,
      ey + Math.sin(a - 0.2) * r * 0.36,
    );
    ctx.closePath();
  }
}

/** Overseer (S156, Act III — redesigned from the S154 "boring star"). An
 *  ORGANIC aura-host: a breathing, blobby body whose lobed outline pulses in and
 *  out, a ring of slithering tentacles reaching for its pack, and a pulsing
 *  nucleus "watching" at the core. Reads instantly as a living controller — NOT
 *  the jammer's hard pentagon-emitter, not any clean polygon. All animation is
 *  keyed off `time` (+ the body's x for per-enemy phase), never random, so the
 *  renderer rebuilds the identical path at the inner-edge scale. */
export function buildOverseer(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
): void {
  const phase = ex * 0.05; // per-enemy phase (stable per body, like the others)
  const spin = time * 0.18; // slow, ominous drift
  // The breathing blob body — a many-lobed outline whose every vertex pulses on
  // its own sine, so the silhouette is soft + alive (never a fixed polygon).
  const lobes = 9;
  for (let i = 0; i < lobes; i++) {
    const a = (i / lobes) * Math.PI * 2 + spin;
    const wob = 0.66 + Math.sin(time * 1.7 + i * 1.9 + phase) * 0.16;
    const x = ex + Math.cos(a) * r * wob;
    const y = ey + Math.sin(a) * r * wob;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  // Slithering tentacles — tapered arms that wave (the host "reaching" for the
  // pack it buffs). Each is a closed sliver from the body edge to a slithering tip.
  const arms = 5;
  for (let i = 0; i < arms; i++) {
    const base = (i / arms) * Math.PI * 2 - spin * 1.3;
    const slither = Math.sin(time * 3 + i * 1.5 + phase) * 0.32; // lateral wave
    const tipA = base + slither;
    const reach = r * (1.16 + Math.sin(time * 2.2 + i + phase) * 0.12);
    ctx.moveTo(
      ex + Math.cos(base + 0.18) * r * 0.5,
      ey + Math.sin(base + 0.18) * r * 0.5,
    );
    ctx.lineTo(ex + Math.cos(tipA) * reach, ey + Math.sin(tipA) * reach);
    ctx.lineTo(
      ex + Math.cos(base - 0.18) * r * 0.5,
      ey + Math.sin(base - 0.18) * r * 0.5,
    );
    ctx.closePath();
  }
  // The watching nucleus — a pulsing core circle.
  circle(ctx, ex, ey, r * (0.26 + Math.sin(time * 2.6 + phase) * 0.06));
}

/** Leech (S158, Act II) — a parasitic siphon-tick: a throbbing gorged abdomen, a
 *  forward proboscis "needle" it drains with, and barbed siphon hooks. Directional
 *  (it points its needle along travel). The throb (it's full of stolen credits)
 *  keys off `time` + x, never random, so the inner-edge rebuild matches. Reads
 *  instantly as "this thing is bleeding you", NOT any wedge/cluster/glyph. */
export function buildLeech(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
  heading: number,
): void {
  const c = Math.cos(heading);
  const s = Math.sin(heading);
  const gorge = 1 + Math.sin(time * 3 + ex * 0.05) * 0.1; // throbbing fed sac
  circle(ctx, ex - 0.25 * r * c, ey - 0.25 * r * s, r * 0.72 * gorge); // abdomen
  circle(ctx, ex + 0.45 * r * c, ey + 0.45 * r * s, r * 0.4); // head
  rMoveTo(ctx, ex, ey, c, s, 1.5 * r, 0); // proboscis tip (forward)
  rLineTo(ctx, ex, ey, c, s, 0.5 * r, 0.16 * r);
  rLineTo(ctx, ex, ey, c, s, 0.5 * r, -0.16 * r);
  ctx.closePath();
  for (let i = -1; i <= 1; i += 2) {
    rMoveTo(ctx, ex, ey, c, s, -0.15 * r, i * 0.58 * r); // barbed siphon hooks
    rLineTo(ctx, ex, ey, c, s, -0.75 * r, i * 1.02 * r);
    rLineTo(ctx, ex, ey, c, s, -0.5 * r, i * 0.55 * r);
    ctx.closePath();
  }
}

/** Regenerator (S158, Act III) — a self-knitting crystalline form: a faceted core
 *  ringed by crystal shards that PULSE in and out (re-assembling), so it reads as
 *  "it keeps healing — out-burst it". The knit pulse keys off `time` (+ x phase),
 *  never random. Clearly NOT the jammer's hard emitter nor the overseer's blob. */
export function buildRegenerator(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
): void {
  const phase = ex * 0.05;
  const spin = time * 0.25;
  polygon(ctx, ex, ey, r * 0.5, 6, spin); // crystalline core
  const shards = 6;
  for (let i = 0; i < shards; i++) {
    const a = (i / shards) * Math.PI * 2 + spin;
    const knit = 0.7 + Math.sin(time * 3 + i * 1.5 + phase) * 0.3; // re-knitting
    const bx = ex + Math.cos(a) * r * 0.5;
    const by = ey + Math.sin(a) * r * 0.5;
    const px = Math.cos(a + 0.4);
    const py = Math.sin(a + 0.4);
    ctx.moveTo(bx + px * r * 0.18, by + py * r * 0.18);
    ctx.lineTo(ex + Math.cos(a) * r * 1.15 * knit, ey + Math.sin(a) * r * 1.15 * knit);
    ctx.lineTo(bx - px * r * 0.18, by - py * r * 0.18);
    ctx.closePath();
  }
}

/** Splitter (S158, Act II/III) — a fracturing/cloning form: FOUR diamond cells
 *  packed into one body with thin fracture gaps that BREATHE apart (it is about to
 *  break into four), so the "kill it → it splits" read is unmistakable. The gap
 *  keys off `time` (+ x phase), never random. */
export function buildSplitter(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
): void {
  const split = 0.16 + Math.sin(time * 2.5 + ex * 0.05) * 0.07; // the crack breathes
  for (let q = 0; q < 4; q++) {
    const a = Math.PI / 4 + (q / 4) * Math.PI * 2; // the four diagonals
    polygon(
      ctx,
      ex + Math.cos(a) * r * split,
      ey + Math.sin(a) * r * split,
      r * 0.52,
      4,
      a,
    ); // a drifting quarter-cell
  }
}

/** Splinter (S158) — the Splitter's sub: a tiny sharp triangular shard. Small +
 *  fast; deliberately minimal so a burst of four reads as fragments, not bodies. */
export function buildShard(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
): void {
  ctx.moveTo(ex, ey - r * 1.1);
  ctx.lineTo(ex + r * 0.72, ey + r * 0.7);
  ctx.lineTo(ex - r * 0.72, ey + r * 0.7);
  ctx.closePath();
}

/** Core Breaker / AXIOM-77 — a bright multi-part core with rotating sub-parts
 *  (scales with r, so the final boss reads biggest). */
export function buildBoss(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  time: number,
): void {
  polygon(ctx, ex, ey, r * 0.5, 8, time * 0.2); // dense octagonal core
  const parts = 3;
  for (let i = 0; i < parts; i++) {
    const a = (i / parts) * Math.PI * 2 + time * 0.6; // orbit
    diamondAt(
      ctx,
      ex + Math.cos(a) * r * 0.85,
      ey + Math.sin(a) * r * 0.85,
      r * 0.3,
    );
  }
}

// ─── Registry ───────────────────────────────────────────────────────────────

/** Dispatch table keyed by `EnemyConfig.shape`. Unknown / missing → caller
 *  falls back to `buildDiamond`. Stealth's "phantom" is intentionally absent
 *  from this dispatch — stealth enemies render via `StealthRenderer`, which
 *  imports `buildPhantom` directly (cloak / white-hot reveal, BL-49). */

/** Hunter-Killer (Task-009 / Spec B, Act II) — a twin-fang mandible pair: two
 *  swept blades converging on a forward point with a narrow thorax between —
 *  reads as "it hunts". Heading-aligned (it is the FAST one; direction is its
 *  identity). Clearly not the scout's single dart, the worm's segments, nor
 *  the phantom's hood: the silhouette's signature is the NEGATIVE SPACE
 *  between the two fangs. */
export function buildFang(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  _time: number,
  heading: number,
): void {
  const c = Math.cos(heading);
  const s = Math.sin(heading);
  for (let i = -1; i <= 1; i += 2) {
    // one swept fang: rear barb → outer bow → forward tip → inner edge back
    rMoveTo(ctx, ex, ey, c, s, -1.05 * r, i * 0.85 * r);
    rLineTo(ctx, ex, ey, c, s, -0.2 * r, i * 1.0 * r); // outer bow
    rLineTo(ctx, ex, ey, c, s, 1.35 * r, i * 0.18 * r); // forward tip
    rLineTo(ctx, ex, ey, c, s, -0.1 * r, i * 0.42 * r); // inner edge
    ctx.closePath();
  }
  // narrow thorax joining the fangs (kept small — the gap must survive)
  rMoveTo(ctx, ex, ey, c, s, -0.9 * r, 0.3 * r);
  rLineTo(ctx, ex, ey, c, s, -0.5 * r, 0);
  rLineTo(ctx, ex, ey, c, s, -0.9 * r, -0.3 * r);
  ctx.closePath();
}

/** Custodian (Task-009 / Spec B, Act III) — an armored vault: a hexagonal
 *  strongbox with a recessed inner keep, heading-agnostic (a vault does not
 *  lunge). Reads as "hard to open — burst it before it re-seals". Clearly not
 *  the golem's plain block (hex + inner keep), the node's ring, nor the
 *  regenerator's pulsing shards (nothing here moves — stillness IS the tell). */
export function buildVault(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
): void {
  // outer hex (flat-top)
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    const x = ex + Math.cos(a) * 1.05 * r;
    const y = ey + Math.sin(a) * 1.05 * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  // recessed inner keep (rotated square — the "lock")
  ctx.moveTo(ex, ey - 0.5 * r);
  ctx.lineTo(ex + 0.5 * r, ey);
  ctx.lineTo(ex, ey + 0.5 * r);
  ctx.lineTo(ex - 0.5 * r, ey);
  ctx.closePath();
}

/** Siphon (Task-009 / Spec B, Act III) — an inverted droplet with a hollow
 *  core and a forward intake spike: the wallet-drainer. Distinct from the
 *  leech's twin-sac + barbed hooks (one smooth drop, one hollow) and from
 *  every faceted family (this one is round-shouldered). The hollow core is
 *  the signature — it is EMPTY, because it empties you. */
export function buildDroplet(
  ctx: CanvasRenderingContext2D,
  ex: number,
  ey: number,
  r: number,
  _time: number,
  heading: number,
): void {
  const c = Math.cos(heading);
  const s = Math.sin(heading);
  // drop body: forward point, round rear — built with two quadratics
  const tipX = ex + 1.3 * r * c;
  const tipY = ey + 1.3 * r * s;
  ctx.moveTo(tipX, tipY);
  ctx.quadraticCurveTo(
    ex + 0.35 * r * c - 1.1 * r * s,
    ey + 0.35 * r * s + 1.1 * r * c,
    ex - 0.85 * r * c - 0.55 * r * s,
    ey - 0.85 * r * s + 0.55 * r * c,
  );
  ctx.quadraticCurveTo(
    ex - 1.35 * r * c,
    ey - 1.35 * r * s,
    ex - 0.85 * r * c + 0.55 * r * s,
    ey - 0.85 * r * s - 0.55 * r * c,
  );
  ctx.quadraticCurveTo(
    ex + 0.35 * r * c + 1.1 * r * s,
    ey + 0.35 * r * s - 1.1 * r * c,
    tipX,
    tipY,
  );
  ctx.closePath();
  // hollow core (the emptied reservoir)
  circle(ctx, ex - 0.3 * r * c, ey - 0.3 * r * s, 0.42 * r);
}

export const ENEMY_SHAPES: Record<string, EnemyShapeFn> = {
  dart: buildDart,
  block: buildBlock,
  cluster: buildCluster,
  worm: buildWorm,
  glyph: buildGlyph,
  ram: buildRam,
  boss: buildBoss,
  diamond: buildDiamond,
  // BL-152 (S154) — fresh distinct families: the retired-glyph `node`, plus the
  // two new per-act threats. No two archetypes share a builder.
  node: buildNode,
  jammer: buildJammer,
  overseer: buildOverseer,
  // BL-155 (S158) — Threat Pack 2: three fresh distinct families + the Splitter's
  // light `shard` sub. No two archetypes share a builder.
  leech: buildLeech,
  regenerator: buildRegenerator,
  splitter: buildSplitter,
  shard: buildShard,
  // Task-009 (Spec B) — the three W20+ data archetypes: three fresh distinct
  // families (BL-168 contract: no two archetypes share a builder).
  fang: buildFang,
  vault: buildVault,
  droplet: buildDroplet,
};
